import { html, when, type SafeHtml } from '../core/safe-html';
import type { Device } from '../models/device';

const PENDING_LABELS: Record<string, string> = {
  restart: 'Yeniden başlatılıyor…',
  rescue: 'Kurtarma moduna alınıyor…',
  force_install: 'Yeniden kurulum başlatılıyor…',
};

/** Onaylanmış komut, sonucu bekleniyor — küçük dönen rozet (liste + detay). */
export function pendingBadge(d: Device): SafeHtml {
  const p = d.pendingAction;
  if (!p) return html``;
  return html`
    <span class="chip" style="background:var(--vx-warning-bg);color:var(--vx-warning-text);font-weight:600">
      <i class="ti ti-loader-2 spin" style="font-size:12px"></i>${PENDING_LABELS[p.kind] ?? p.kind}
      <span style="opacity:.7;font-weight:500">${p.ageSec} sn</span>
    </span>`;
}

/**
 * Kurtarma/kurulum ilerleme göstergesi — liste ve detayda ortak.
 * compact=true: tek satır çip (liste); false: bar + adım (detay kartı).
 */
export function rescueBadge(d: Device, compact: boolean): SafeHtml {
  const r = d.rescue;
  if (d.status !== 'rescue' || !r) return html``;
  const pct = r.pct == null ? null : Math.max(0, Math.min(100, r.pct));
  const failed = r.pct != null && r.pct < 0;
  const done = r.pct != null && r.pct >= 100;
  const installing = r.phase === 'install' && !failed && !done;
  const stale = r.ageSec != null && r.ageSec > 180;
  // %100 sonrası cihaz reboot'ta: kernel+systemd+WG ~45-60 sn sürer, sonra
  // Debian kendini bildirir ve bu rozet kaybolur. Kullanıcıya bunu söyle.
  const label = failed
    ? `Kurulum HATA — ${r.step ?? ''}`
    : done
      ? `Kurulum tamam · Debian açılıyor… (${r.ageSec ?? 0} sn)`
      : installing
        ? `${r.step ?? 'kuruluyor'} · %${pct ?? 0}`
        : 'Kurtarma menüsünde bekliyor';
  const icon = failed ? 'ti ti-alert-triangle' : (installing || done) ? 'ti ti-loader-2 spin' : 'ti ti-lifebuoy';

  if (compact) {
    return html`
      <span class="chip" style="background:${failed ? 'var(--vx-danger)' : done ? 'var(--vx-success-bg)' : 'var(--vx-danger-bg)'};color:${failed ? '#fff' : done ? 'var(--vx-success-text)' : 'var(--vx-danger-text)'};font-weight:600;gap:6px">
        <i class="${icon}" style="font-size:12px"></i>${label}
        ${when(installing && pct != null, () => html`
          <span style="width:56px;height:5px;border-radius:999px;background:rgba(0,0,0,.12);overflow:hidden;display:inline-block">
            <span style="display:block;height:100%;width:${pct}%;background:var(--vx-danger);transition:width .4s var(--vx-ease)"></span>
          </span>`)}
        ${when(stale, () => html`<span style="opacity:.7;font-weight:500">· rapor ${Math.round((r.ageSec ?? 0) / 60)} dk önce</span>`)}
      </span>`;
  }

  return html`
    <div class="card" style="flex:1 1 100%;min-width:0;border-color:${failed ? 'var(--vx-danger)' : done ? 'var(--vx-success)' : 'var(--vx-danger-bg)'};background:${failed ? 'var(--vx-danger-bg)' : done ? 'var(--vx-success-bg)' : 'var(--vx-surface)'}">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:${installing ? '10px' : '0'}">
        <span class="chip" style="background:${done ? 'var(--vx-success)' : 'var(--vx-danger)'};color:#fff;font-weight:600"><i class="${icon}" style="font-size:12px"></i>${done ? 'kurulum tamam' : installing ? 'kurulum sürüyor' : failed ? 'kurulum hatası' : 'kurtarma modu'}</span>
        <span style="font:600 14px var(--vx-font-sans)">${label}</span>
        <span class="hs" style="margin-left:auto;color:var(--vx-text-3)"><i class="ti ti-clock"></i>rapor ${r.ageSec != null ? `${r.ageSec} sn önce` : '—'}</span>
      </div>
      ${when(installing && pct != null, () => html`
        <div style="height:8px;border-radius:999px;background:var(--vx-track);overflow:hidden">
          <span style="display:block;height:100%;border-radius:999px;background:var(--vx-primary);width:${pct}%;transition:width .4s var(--vx-ease)"></span>
        </div>
        <div class="mono" style="font-size:11.5px;color:var(--vx-text-3);margin-top:6px">%${pct} · imaj iner → sha256 → disk → yazım → önyükleme → yeniden başlatma</div>`)}
      ${when(done, () => html`
        <div style="font:400 12px/1.5 var(--vx-font-sans);color:var(--vx-text-2);margin-top:8px">
          İmaj yazıldı, cihaz yeniden başlıyor. Debian açılıp tünel kalkınca (~45-60 sn) kendini bildirir ve bu kart kaybolur.
        </div>`)}
      ${when(!installing && !failed && !done, () => html`
        <div style="font:400 12px/1.5 var(--vx-font-sans);color:var(--vx-text-2);margin-top:8px">
          Cihaz RAM-OS kurtarma ortamında. Ağ varsa 30 sn içinde otomatik yeniden kurulum başlar; ilerleme burada görünür.
        </div>`)}
    </div>`;
}

const WG_MODE_LABEL: Record<string, { t: string; bg: string; fg: string; icon: string }> = {
  udp51820: { t: 'WG udp/51820', bg: 'var(--vx-success-bg)', fg: 'var(--vx-success-text)', icon: 'ti ti-lock' },
  udp443: { t: 'WG udp/443', bg: 'var(--vx-info-bg)', fg: 'var(--vx-info-text)', icon: 'ti ti-lock' },
  tcp443: { t: 'WG tcp/443 (wstunnel)', bg: 'var(--vx-warning-bg)', fg: 'var(--vx-warning-text)', icon: 'ti ti-lock-cog' },
  'https-only': { t: 'HTTPS-only (tünel yok)', bg: 'var(--vx-danger-bg)', fg: 'var(--vx-danger-text)', icon: 'ti ti-lock-off' },
};

/** Bağlantı modu rozeti (tünel yolu). */
export function wgModeBadge(d: Device, compact = true): SafeHtml {
  const m = d.net.wgMode;
  if (!m) return html``;
  const s = WG_MODE_LABEL[m] ?? { t: m, bg: 'var(--vx-chip-bg)', fg: 'var(--vx-chip-fg)', icon: 'ti ti-network' };
  return html`<span class="chip" title="Tünel yolu" style="background:${s.bg};color:${s.fg};font-weight:600;${compact ? 'font-size:10.5px;padding:3px 8px' : ''}"><i class="${s.icon}" style="font-size:12px"></i>${s.t}</span>`;
}
